/* eslint-disable react/prop-types */
import moment from 'moment';
import React from 'react';
import { useTheme } from 'styled-components';
import DateCard, { eventDateCardProps } from '../DateCard';
import EventStatusChips from '../EventStatusChips';
import Icon from '../Icon';
import { tokens, orange, inkA } from '../tokens';

// Gen-3 mobile event card — boxed date card, name, venue line, and status
// chips up top; the on-sale chip and big labeled action tiles below. Spec
// source: users-accounts.jsx EventRow (mobile branch) + EventActionTile.

// Large stacked action tile so the actions are big and evenly spread.
function ActionTile({ icon, label, onClick }) {
  const theme = useTheme();
  const t = tokens(theme);
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        height: 64,
        borderRadius: 13,
        cursor: 'pointer',
        background: inkA(theme, 0.05),
        border: '1px solid ' + t.border,
        padding: 0,
      }}
    >
      <Icon name={icon} size={23} color={orange(1)} strokeWidth={2.15} />
      <span
        style={{
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: '0.2px',
          color: t.sub,
        }}
      >
        {label}
      </span>
    </button>
  );
}

// "On sale Mar 13, 2026" pill — stored wall-clock date shown verbatim;
// events without a sale date read "Not on sale".
function OnSaleChip({ saleStart }) {
  const theme = useTheme();
  const t = tokens(theme);
  const parsed = saleStart ? moment(saleStart, 'MM-DD-YYYY') : null;
  const chipBase = {
    alignSelf: 'flex-start',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '4px 10px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 700,
  };
  if (!parsed || !parsed.isValid()) {
    return (
      <span
        style={{
          ...chipBase,
          background: inkA(theme, 0.05),
          border: '1px solid ' + t.border,
          color: t.sub,
        }}
      >
        Not on sale
      </span>
    );
  }
  return (
    <span
      style={{
        ...chipBase,
        background: orange(0.13),
        border: '1px solid ' + orange(0.55),
        color: orange(1),
      }}
    >
      <Icon name="circle-play" size={12} color={orange(1)} />
      On sale {parsed.format('MMM D, YYYY')}
    </span>
  );
}

const EventCardMobile = ({
  event,
  kind,
  last,
  onWeather,
  onSnapshot,
  onManage,
}) => {
  const theme = useTheme();
  const t = tokens(theme);
  const dateProps = eventDateCardProps(event);

  return (
    <div style={{ borderBottom: last ? 'none' : '1px solid ' + t.line }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 11,
          padding: '15px 16px',
        }}
      >
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          {dateProps ? <DateCard {...dateProps} /> : null}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: t.text,
                lineHeight: 1.3,
              }}
            >
              {event.name}
            </div>
            {event.track && event.track.name ? (
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  fontSize: 13.5,
                  color: t.sub,
                  marginTop: 4,
                }}
              >
                <Icon name="map-pin" size={14} color={orange(1)} />
                {event.track.name}
              </div>
            ) : null}
            <div style={{ marginTop: 9 }}>
              <EventStatusChips event={event} kind={kind} direction="row" />
            </div>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 11,
            paddingTop: 11,
            borderTop: '1px solid ' + t.line,
          }}
        >
          <OnSaleChip saleStart={event.sale_start} />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: onWeather ? '1fr 1fr 1fr' : '1fr 1fr',
              gap: 9,
            }}
          >
            {onWeather ? (
              <ActionTile
                icon="cloud-lightning"
                label="Weather"
                onClick={onWeather}
              />
            ) : null}
            <ActionTile icon="camera" label="Snapshot" onClick={onSnapshot} />
            <ActionTile
              icon="sliders-horizontal"
              label="Manage"
              onClick={onManage}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventCardMobile;
